import type { Me, PermissionCode } from '@pm/schemas';
import {
  BarChart3,
  Briefcase,
  Building2,
  Calendar,
  CalendarRange,
  FileText,
  Folder,
  Inbox,
  Kanban,
  LayoutDashboard,
  ListTodo,
  Lock,
  MailQuestion,
  NotebookPen,
  type LucideIcon,
  Settings,
  Users,
  Workflow,
} from 'lucide-react';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Qualquer uma destas libera o item. Vazio = sempre visível. */
  permissions?: PermissionCode[];
  /** Só aparece em Agency Mode. */
  agencyOnly?: boolean;
  group?: 'main' | 'personal';
  shortcut?: string;
}

/** Ordem de FRONTEND_UX §2. */
const ITEMS: NavItem[] = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    permissions: ['reports.view'],
    shortcut: 'G D',
  },
  { to: '/my-work', label: 'Meu Trabalho', icon: Inbox, shortcut: 'G M' },
  { to: '/demandas', label: 'Demandas', icon: ListTodo, shortcut: 'G T' },
  { to: '/pipelines', label: 'Pipelines', icon: Kanban, shortcut: 'G P' },
  { to: '/semana', label: 'Semana', icon: CalendarRange, shortcut: 'G S' },
  {
    to: '/clientes',
    label: 'Clientes',
    icon: Building2,
    permissions: ['clients.view'],
    agencyOnly: true,
  },
  { to: '/contextos', label: 'Contextos', icon: Folder, permissions: ['clients.view'] },
  { to: '/times', label: 'Times', icon: Users, permissions: ['teams.view'] },
  { to: '/workflows', label: 'Workflows', icon: Workflow, permissions: ['workflows.view'] },
  { to: '/calendario', label: 'Calendário', icon: Calendar },
  {
    to: '/formularios',
    label: 'Formulários',
    icon: FileText,
    permissions: ['forms.view'],
    agencyOnly: true,
  },
  {
    to: '/comercial',
    label: 'Comercial',
    icon: Briefcase,
    permissions: ['proposals.view', 'contracts.view', 'services.view'],
    agencyOnly: true,
  },
  {
    to: '/triagem',
    label: 'Triagem',
    icon: MailQuestion,
    permissions: ['external_requests.triage'],
    agencyOnly: true,
  },
  { to: '/relatorios', label: 'Relatórios', icon: BarChart3, permissions: ['reports.view'] },
  {
    to: '/admin',
    label: 'Administração',
    icon: Settings,
    permissions: ['settings.manage', 'users.manage_roles'],
  },
  { to: '/pessoal', label: 'Minhas tarefas', icon: Lock, group: 'personal' },
  { to: '/anotacoes', label: 'Minhas anotações', icon: NotebookPen, group: 'personal' },
];

/** Filtra por modo da organização e permissões (módulos aparecem conforme features/permissões). */
export function navItemsFor(me: Me): NavItem[] {
  const mode = me.organization?.mode ?? 'INTERNAL';
  return ITEMS.filter((item) => {
    if (item.agencyOnly && mode !== 'AGENCY') return false;
    // "Contextos" aparece nos dois modos: na agência mostra projetos, departamentos e áreas
    // internas (os clientes ficam em "Clientes").
    if (!item.permissions?.length) return true;
    return item.permissions.some((p) => me.permissions.includes(p));
  });
}
